import moment from 'moment';
import React, { ReactNode, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { FormattedMessage } from 'react-intl';

import { ReactComponent as CopyIcon } from '@/assets/icons/copy.svg';
import { ReactComponent as DoneIcon } from '@/assets/icons/done.svg';
import Tooltip, { TooltipPlacement } from '@/components/output/Tooltip';
import { useTimer } from '@/hooks';

export interface CopyToClipboardIconProps extends Omit<CopyToClipboard.Props, 'children'> {
  IconProps?: React.SVGProps<SVGSVGElement> & { title?: string };
  messages?: {
    copy?: ReactNode;
    copied?: ReactNode;
  };
  tooltipPlacement?: {
    copy?: TooltipPlacement;
    copied?: TooltipPlacement;
  };
}

const CopyToClipboardIcon: React.FC<CopyToClipboardIconProps> = ({
  IconProps,
  messages,
  tooltipPlacement,
  ...props
}) => {
  const [copiedAt, setCopiedAt] = useState<Date | undefined>();
  const [opened, setOpened] = useState<boolean>(false);
  const [copiedTimer] = useTimer(copiedAt);
  return copiedTimer && copiedTimer > 0 ? (
    <Tooltip
      open
      disableFocusListener
      disableHoverListener
      disableTouchListener
      placement={tooltipPlacement?.copied || 'top'}
      arrow={false}
      title={messages?.copied || <FormattedMessage id="common.components.copyToClipboard.copiedTooltip" />}
    >
      <DoneIcon {...IconProps} />
    </Tooltip>
  ) : (
    <Tooltip
      open={opened}
      onOpen={(): void => setOpened(true)}
      onClose={(): void => setOpened(false)}
      placement={tooltipPlacement?.copy || 'top'}
      title={messages?.copy || <FormattedMessage id="common.components.copyToClipboard.copyTooltip" />}
    >
      <CopyToClipboard
        {...props}
        onCopy={(): void => {
          setCopiedAt(
            moment(new Date())
              .add(3, 's')
              .toDate(),
          );
          setOpened(false);
        }}
      >
        <CopyIcon
          {...IconProps}
          style={{
            cursor: 'pointer',
            ...(IconProps?.style ? IconProps.style : {}),
          }}
        />
      </CopyToClipboard>
    </Tooltip>
  );
};

export default CopyToClipboardIcon;
